import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { useMemo, useState, useCallback } from "react";

interface WorkOrderMapProps {
  occurrences: Array<{
    id: string;
    protocol: string;
    address: string;
    latitude?: string;
    longitude?: string;
  }>;
}

const libraries: ("places" | "drawing" | "geometry")[] = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

export const WorkOrderMap = ({ occurrences }: WorkOrderMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    language: "pt-BR",
    region: "BR",
  });

  // Filtrar ocorrências que têm coordenadas válidas
  const validOccurrences = useMemo(() => {
    return occurrences.filter(
      (occ) => occ.latitude && occ.longitude
    );
  }, [occurrences]);

  // Calcular centro do mapa baseado nas ocorrências
  const mapCenter = useMemo(() => {
    if (validOccurrences.length === 0) {
      return { lat: -3.7588, lng: -40.8135 }; // Centro de Frecheirinha
    }

    const avgLat =
      validOccurrences.reduce((sum, occ) => sum + parseFloat(occ.latitude!), 0) /
      validOccurrences.length;
    const avgLng =
      validOccurrences.reduce((sum, occ) => sum + parseFloat(occ.longitude!), 0) /
      validOccurrences.length;

    return { lat: avgLat, lng: avgLng };
  }, [validOccurrences]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (loadError) {
    return (
      <div className="w-full h-[400px] bg-gray-100 rounded-lg flex items-center justify-center">
        <p className="text-red-500">Erro ao carregar o mapa</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-[400px] bg-gray-100 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">Carregando mapa...</p>
      </div>
    );
  }

  if (validOccurrences.length === 0) {
    return (
      <div className="w-full h-[400px] bg-gray-100 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">
          Nenhuma ocorrência com localização disponível
        </p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      mapContainerClassName="rounded-lg"
      center={mapCenter}
      zoom={validOccurrences.length === 1 ? 16 : 14}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        zoomControl: true,
      }}
    >
      {validOccurrences.map((occurrence, index) => (
        <Marker
          key={occurrence.id}
          position={{
            lat: parseFloat(occurrence.latitude!),
            lng: parseFloat(occurrence.longitude!),
          }}
          title={`${occurrence.protocol} - ${occurrence.address}`}
          label={{
            text: `${index + 1}`,
            color: "white",
            fontSize: "14px",
            fontWeight: "bold",
          }}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 20,
            fillColor: "#FF6B6B",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
          }}
        />
      ))}
    </GoogleMap>
  );
};
